/* eslint-disable array-callback-return */
import React from 'react';

import arrayMove from 'array-move';
import { MenuOutlined } from '@ant-design/icons';
import { Row, Col, Table } from 'antd';
import { compose } from '../../core';
import {
  getIntFromString
} from '../../look';
import { SortableContainer as BaseSortableContainer, SortableElement as BaseSortableElement, SortableHandle as BaseSortableHandle } from 'react-sortable-hoc';
import { withEditChoice } from './SurveyOperations';
import { withApollo } from '@apollo/client/react/hoc';
import { ReactMarkdown } from '../../look';
import styled from 'styled-components';
import '../css/survey-admin-panel.css';
import { CHOICE_SUBSCRIPTION } from '../graphql/ChoiceSubscription.gql';

// Define sortable components
const SortableItem = BaseSortableElement(props => <tr style={{ border: "1px solid #f5f5f5", borderRadius: '5px', width: "100%" }} {...props} />);
const SortableContainer = BaseSortableContainer(props => <tbody {...props} />);
const DragHandle = BaseSortableHandle(() => (
  <MenuOutlined
    style={{ cursor: 'grab', color: '#999', margin: '-16px', padding: '16px', width: '100%' }}
    onPointerEnterCapture={() => { }}
    onPointerLeaveCapture={() => { }}
  />
));

const ChoiceTable = props => {
  const { record, editChoice, client } = props;
  const { choiceSet } = record;
  const [dataSource, setDataSource] = React.useState([]);
  const [dragLoading, setDragLoading] = React.useState(false);
  const [pendingDragOrder, setPendingDragOrder] = React.useState(null);

  const choiceDataRef = React.useRef(null);
  const m = React.useRef(true);
  const choiceSubscriptionRef = React.useRef(null);

  // Subscribe to choice updates
  React.useEffect(() => {
    SubscribeToChoice();
    return () => {
      if (choiceSubscriptionRef.current) {
        choiceSubscriptionRef.current.unsubscribe();
        choiceSubscriptionRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const SubscribeToChoice = () => {
    choiceSubscriptionRef.current = client.subscribe({
      query: CHOICE_SUBSCRIPTION,
    }).subscribe({
      next(result) {
        if (m.current) {
          const updatedChoice = result.data.surveyQuestionChoiceSubscription?.choice;
          // Update dataSource when a choice is updated
          setDataSource(currentDataSource => {
            const choiceIndex = currentDataSource?.findIndex((choice) => choice?.id === updatedChoice.id);

            if (choiceIndex >= 0) {
              // Check if the data actually changed
              const currentChoice = currentDataSource[choiceIndex];
              const hasChanged = JSON.stringify(currentChoice) !== JSON.stringify(updatedChoice);

              if (hasChanged) {
                const newDataSource = [...currentDataSource];
                newDataSource[choiceIndex] = {
                  ...newDataSource[choiceIndex],
                  ...updatedChoice,
                  questionText: newDataSource[choiceIndex].questionText,
                  label: newDataSource[choiceIndex].label
                };
                choiceDataRef.current = newDataSource;
                if (dragLoading && pendingDragOrder) {
                  const currentOrder = newDataSource.map(item => item.id);
                  const orderMatches = JSON.stringify(currentOrder) === JSON.stringify(pendingDragOrder);
                  if (orderMatches) {
                    setDragLoading(false);
                    setPendingDragOrder(null);
                  }
                }
                return newDataSource;
              }
            }
            return currentDataSource;
          });
        }
      }
    });
  };

  React.useEffect(() => {
    const edges = [];
    choiceSet &&
      choiceSet.edges.length > 0 &&
      choiceSet.edges.map(({ node }, index) => {
        edges.push({ ...node, index: index + 1 });
      });

    const sortedEdges = edges.sort((a, b) => a.sequence - b.sequence);
    choiceDataRef.current = sortedEdges;
    setDataSource(sortedEdges);
  }, [choiceSet]);

  const onSort = async sortedItems => {
    setDragLoading(true);
    try {
      await Promise.all(
        sortedItems.map(async i => {
          // Only update the sequence, not the content
          await editChoice({ id: getIntFromString(i.id), sequence: i.index + 1 });
        })
      );
    } catch (e) {
      setDragLoading(false);
      throw Error(e.message);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMove([].concat(dataSource), oldIndex, newIndex).filter(el => !!el);
      const indexData = [];
      newData.map((node, index) => {
        // Preserve all original data while updating index
        const data = { ...newData[index], index: index };
        indexData.push(data);
      });
      // Only update state if the order actually changed
      const currentOrder = dataSource.map(item => item.id);
      const newOrder = indexData.map(item => item.id);
      const orderChanged = JSON.stringify(currentOrder) !== JSON.stringify(newOrder);

      if (orderChanged) {
        // Start drag loading and store pending order
        setDragLoading(true);
        setPendingDragOrder(newOrder);
        setDataSource(indexData);
        onSort && onSort(indexData);
        // Fallback timeout to stop loading after 10 seconds if no subscription update
        setTimeout(() => {
          setDragLoading(false);
          setPendingDragOrder(null);
        }, 10000);
      }
    }
  };

  const DraggableContainer = props => (
    <SortableContainer useDragHandle disableAutoscroll helperClass="row-dragging" onSortEnd={onSortEnd} {...props} />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    const index = dataSource && dataSource.findIndex(x => x.index === restProps['data-row-key']);
    return <SortableItem style={{ border: "1px solid #f5f5f5", borderRadius: '5px', width: "100%" }} index={index} {...restProps} />;
  };

  const columns = [
    {
      title: 'Sort',
      dataIndex: 'sort',
      width: '6%',
      className: 'drag-visible',
      render: () => <DragHandle />
    },
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => record && <ReactMarkdown style={{ margin: '0.5em 0' }}>{record.label}</ReactMarkdown>
    },
    {
      title: 'help',
      key: 'help',
      render: (text, record) => record && <>{record.inputType}</>
    },
    {
      title: 'Link',
      key: 'link',
      render: (text, record) => (
        <Row gutter={[24, 24]} justify="end" style={{ width: '100%' }}>
          <Col>
          </Col>
        </Row>
      )
    }
  ];


  return (
    <MyTable
      className="expand choice-table-custom"
      showHeader={false}
      tableLayout={'auto'}
      rowKey={'index'}
      pagination={false}
      columns={columns}
      loading={dragLoading}
      dataSource={
        dataSource &&
        dataSource.length > 0 &&
        dataSource.sort((a, b) => (a.sequence < b.sequence ? -1 : a.sequence > b.sequence ? 1 : 0))
      }
      components={{
        body: {
          wrapper: DraggableContainer,
          row: DraggableBodyRow
        }
      }}
    />
  );
};

export default compose(withEditChoice, withApollo)(ChoiceTable);

const MyTable = styled(Table)`
  &.choice-table-custom {
    .drag-visible {
      width: 6% !important;
      min-width: 6% !important;
      max-width: 6% !important;
    }
    
    .ant-table-row-expand-icon-cell {
      width: 6% !important;
      min-width: 6% !important;
      max-width: 6% !important;
    }
  }
`;
